/**
 * Revenue statement money + status helpers.
 *
 * The flow is: imported statement (Processing while the import→ledger pipeline
 * runs) → Draft (estimated value, awaiting confirmation) → confirm amount
 * received → Settled (locked). A statement is "Settled" once an actual amount
 * received has been entered; until then it is a "Draft" showing an estimate.
 *
 *  - estimatedValue: the statement value as reported on the imported statement.
 *  - actualReceived: the confirmed amount (null while still a Draft).
 */
import { formatMonthYear } from "@/lib/date";
import { formatAmountWithCodeFromString, toNumber } from "@/lib/helpers";
import { REVENUE_MESSAGES } from "@/lib/trackMessages";
import type { RevenueStatement, RevenueStatementStatus } from "@/types";

export type RevenueDisplayStatus = "processing" | "failed" | "draft" | "settling" | "settled";

/**
 * Statuses still moving through the import→ledger pipeline. Draft, processed,
 * and failed are terminal (a failed processing run can land back on draft, so
 * it must not keep polling alive).
 */
export function isStatementProcessing(status: RevenueStatementStatus): boolean {
  return status === "generating" || status === "ready";
}

/**
 * Whether background work (import→ledger pipeline or an async settle task)
 * still owns the statement — the polling predicate.
 */
export function isStatementBusy(
  statement: Pick<RevenueStatement, "status" | "is_settling">,
): boolean {
  return isStatementProcessing(statement.status) || statement.is_settling;
}

/**
 * The "Statement value" display string. While generating, revenue still holds
 * the backend default 0 — indistinguishable from a real zero-revenue statement
 * by value alone — so it is masked with "-". A failed statement holds that same
 * 0 for good: nothing was attributed, which is not the same as no revenue, so
 * it is masked too. Revenue is parsed from the file by the time the statement
 * reaches "ready", so of the rest only "generating" is masked.
 */
export function formatStatementValue(
  statement: Pick<RevenueStatement, "status" | "revenue" | "currency">,
  locale: string,
): string {
  if (statement.status === "generating" || statement.status === "failed") return "-";
  return formatAmountWithCodeFromString(statement.revenue, statement.currency, locale);
}

export const REVENUE_STATUS_LABEL: Record<RevenueDisplayStatus, string> = {
  processing: "Processing",
  failed: "Failed",
  draft: "Draft",
  settling: "Settling",
  settled: "Settled",
};

/**
 * Derive the display status from a statement. "Failed" when the import or the
 * revenue calculation behind it gave up, "Processing" while the import→ledger
 * pipeline is still running, "Settling" while a queued settlement task runs,
 * "Settled" once an actual amount received has been entered, "Draft" in between.
 */
export function getRevenueDisplayStatus(
  statement: Pick<RevenueStatement, "is_settled" | "is_settling" | "status"> | undefined,
): RevenueDisplayStatus {
  if (!statement) return "draft";
  // Terminal, and nothing else can be true of it: a failed run books no
  // receivable, so it can neither be settling nor settled.
  if (statement.status === "failed") return "failed";
  if (statement.is_settled) return "settled";
  if (statement.is_settling) return "settling";
  if (isStatementProcessing(statement.status)) return "processing";
  return "draft";
}

/**
 * Map the draft/settled multi-select to the API's `is_settled` boolean. Selecting
 * both (or neither) means "no settlement filter", so return `undefined` to omit it.
 */
export function deriveSettledFilter(statuses: RevenueDisplayStatus[]): boolean | undefined {
  const isDraft = statuses.includes("draft");
  const isSettled = statuses.includes("settled");
  if (isDraft === isSettled) return undefined;
  return isSettled;
}

/**
 * Signed settlement difference: the sum of the persisted difference components
 * (quantity + FX), negated so negative = shortfall, positive = excess. Returns
 * null until an amount is received (i.e. while the statement is still a Draft).
 *
 * FX semantics changed with effective-rate re-booking (Aug 2026): foreign
 * statements now re-book at the rate the cash converted at, so a pure rate
 * move is no longer a "difference" (the books restate to the received amount)
 * and settlement_fx_difference holds only quantization residue. Rows settled
 * before the change — and pre-gate statements whose posted journals kept the
 * spot booking — still carry the full historical rate difference, so the same
 * economics can display differently across old and new rows.
 */
export function getSettlementDifference(
  statement:
    | Pick<
        RevenueStatement,
        | "revenue"
        | "amount_received"
        | "settlement_quantity_difference"
        | "settlement_fx_difference"
      >
    | undefined,
): number | null {
  if (!statement || statement.amount_received == null) return null;
  // Backend persists both difference components in label currency at settle
  // time (FX-safe, positive = shortfall/loss — negate to keep this helper's
  // sign convention). Their sum is the total expected-vs-received difference.
  if (statement.settlement_quantity_difference != null) {
    return -(
      toNumber(statement.settlement_quantity_difference) +
      toNumber(statement.settlement_fx_difference ?? "0")
    );
  }
  // Legacy settlements predating the persisted field; only correct when the
  // statement is in the label currency.
  return toNumber(statement.amount_received) - toNumber(statement.revenue);
}

/** Tailwind text-color class for a signed settlement difference. */
export function getSettlementDifferenceColorClass(difference: number): string {
  if (difference < 0) return "text-red-900";
  if (difference > 0) return "text-green-900";
  return "text-foreground";
}

/**
 * Whether an entered amount (a form-field string) differs from the statement's
 * reported value by at least one cent. Empty or non-numeric input counts as no
 * difference. Rounds to whole cents so floating-point noise (e.g. 1e-14) and
 * sub-cent residue aren't treated as a difference.
 */
export function hasSettlementDifference(statementValue: string, amount: string): boolean {
  if (!amount) return false;
  const entered = Number(amount);
  if (Number.isNaN(entered)) return false;
  return Math.round((entered - toNumber(statementValue)) * 100) !== 0;
}

export interface SettlementAmounts {
  /**
   * Statement-currency value the settle action validates against
   * (backend-quantized to 2dp). expected_foreign_amount is null for
   * home-currency statements (expected_amount is already statement-currency
   * there). Both are null only for statements that can't be settled (the
   * backend only exposes them while processed and unsettled, and rejects
   * settling anything else) — the settle UI is gated to those, so the empty
   * fallback should never be user-visible.
   */
  statementValueRaw: string;
  /** Numeric statementValueRaw; NaN when unparseable. */
  statementAmount: number;
  /** Numeric entered amount; NaN while empty or invalid. */
  enteredAmount: number;
  /**
   * Entered amount differs from the booked receivable (expected_amount) by at
   * least a cent. False when expected_amount is null (statement not settleable
   * yet — the backend's own validation is the authority then). expected_amount
   * is in the label currency, so the same comparison covers both home- and
   * foreign-currency statements.
   */
  hasDifference: boolean;
  /** Signed entered − expected (label currency); null when no difference. */
  difference: number | null;
  /** Effective rate implied by the entry (entered ÷ statement value); null unless both are positive. */
  effectiveRate: number | null;
}

/**
 * Pure derivation of the settle-drawer amounts from a statement and the raw
 * amount-received input, so the arithmetic is unit-testable.
 */
export function deriveSettlementAmounts(
  statement:
    | Pick<RevenueStatement, "expected_amount" | "expected_foreign_amount">
    | null
    | undefined,
  amountStr: string,
): SettlementAmounts {
  const enteredAmount = amountStr ? Number(amountStr) : NaN;
  if (!statement) {
    return {
      statementValueRaw: "",
      statementAmount: NaN,
      enteredAmount,
      hasDifference: false,
      difference: null,
      effectiveRate: null,
    };
  }
  const statementValueRaw = statement.expected_foreign_amount ?? statement.expected_amount ?? "";
  const statementAmount = parseFloat(statementValueRaw);
  const hasDifference =
    statement.expected_amount != null &&
    hasSettlementDifference(statement.expected_amount, amountStr);
  return {
    statementValueRaw,
    statementAmount,
    enteredAmount,
    hasDifference,
    difference: hasDifference ? enteredAmount - Number(statement.expected_amount) : null,
    effectiveRate:
      enteredAmount > 0 && statementAmount > 0 ? enteredAmount / statementAmount : null,
  };
}

/**
 * The statement-currency value settling is judged against: the booked
 * receivable, via its foreign counterpart for cross-currency statements.
 * Null for statements that aren't settleable (settled/processing) — the
 * backend only exposes the receivable while processed and unsettled.
 */
export function getSettlementValue(
  statement: Pick<RevenueStatement, "expected_amount" | "expected_foreign_amount">,
): string | null {
  return statement.expected_foreign_amount ?? statement.expected_amount;
}

/** Sum one expected_* field to a 2dp string; null when no statement has it. */
export function sumExpectedField(
  statements: RevenueStatement[],
  field: "expected_amount" | "expected_foreign_amount",
): string | null {
  const values = statements.map((s) => s[field]).filter((v): v is string => v != null);
  if (values.length === 0) return null;
  return values.reduce((acc, v) => acc + toNumber(v), 0).toFixed(2);
}

/** Resolve the reason to persist: for "Other" the typed description doubles as the free-text reason. */
export function resolveDifferenceReason(reason: string, description: string): string {
  return reason === "Other" ? description.trim() || "Other" : reason;
}

export interface RevenueTotals {
  /** Sum of every statement's reported value. */
  totalRevenue: number;
  /** Sum of the amount received across settled statements. */
  revenueReceived: number;
  /** Sum of the reported value across statements not yet settled. */
  pendingConfirmation: number;
}

export interface BulkSelectionSummary {
  count: number;
  /** Distinct vendor buckets; statements without a vendor share one bucket. */
  vendorCount: number;
  isSingleVendor: boolean;
  /**
   * Sum of the selection's booked receivables (see getSettlementValue). Null
   * when currencies are mixed or nothing in the selection is settleable —
   * unsettleable rows (settled/processing) never contribute.
   */
  sum: number | null;
  /** The shared statement currency, or null when mixed. */
  currency: string | null;
  /** Every selected statement can be settled together (one vendor, one currency, all settleable). */
  canSettleAll: boolean;
  /** Why the selection can't be settled, for the disabled-button tooltip. Null when it can. */
  cannotSettleReason: string | null;
}

/**
 * Summarize a bulk selection for the action bar. Settlement is per-vendor, so
 * "Mark as settled" only applies when the whole selection shares one vendor
 * (and one currency), and every statement is individually settleable: a draft
 * with expected_amount booked (null until processing books the receivable; the
 * backend rejects settling unprocessed statements).
 */
export function getBulkSelectionSummary(statements: RevenueStatement[]): BulkSelectionSummary {
  const vendors = new Set(statements.map((s) => s.vendor ?? ""));
  const currencies = new Set(statements.map((s) => s.currency));
  const isSingleVendor = vendors.size === 1;
  const isSingleCurrency = currencies.size === 1;
  // Statement-currency booked receivables; rows without one (settled or still
  // processing) are excluded — settling is disabled for those selections and a
  // mixed-basis sum would mean nothing.
  const settlementValues = statements
    .map((s) => getSettlementValue(s))
    .filter((v): v is string => v != null);
  const sum =
    isSingleCurrency && settlementValues.length > 0
      ? settlementValues.reduce((acc, v) => acc + toNumber(v), 0)
      : null;

  let cannotSettleReason: string | null = null;
  if (statements.length > 0) {
    if (!isSingleVendor) {
      cannotSettleReason = REVENUE_MESSAGES.bulkSettleMultipleVendors;
    } else if (statements.some((s) => getRevenueDisplayStatus(s) === "settled")) {
      cannotSettleReason = REVENUE_MESSAGES.bulkSettleAlreadySettled;
    } else if (statements.some((s) => getRevenueDisplayStatus(s) === "settling")) {
      // A queued settle task already owns the statement — the backend rejects a
      // second one (ensure_not_settling), and its receivable is about to change.
      cannotSettleReason = REVENUE_MESSAGES.bulkSettleSettling;
    } else if (
      statements.some(
        (s) => getRevenueDisplayStatus(s) === "processing" || s.expected_amount == null,
      )
    ) {
      cannotSettleReason = REVENUE_MESSAGES.bulkSettleProcessing;
    } else if (statements.length === 1 && sum != null && sum < 0) {
      // A lone correction settles automatically with the vendor's next paying
      // statement; it needs that statement in the selection to settle here.
      cannotSettleReason = REVENUE_MESSAGES.bulkSettleSingleCorrection;
    } else if (sum != null && sum < 0) {
      cannotSettleReason = REVENUE_MESSAGES.bulkSettleNegativeSum;
    }
  }

  return {
    count: statements.length,
    vendorCount: vendors.size,
    isSingleVendor,
    sum,
    currency: isSingleCurrency ? [...currencies][0] : null,
    // A vendor reports in one currency, so mixed currencies shouldn't occur for
    // a single-vendor selection — guard silently anyway rather than tooltip it.
    canSettleAll: statements.length > 0 && cannotSettleReason == null && isSingleCurrency,
    cannotSettleReason,
  };
}

/** "Vendor - Month Year" when both resolve, else the statement/file name. */
export function statementCoveredTitle(
  statement: Pick<RevenueStatement, "vendor" | "period_end" | "name" | "file_name">,
  getVendorNameById: (id: string | null | undefined) => string | undefined,
): string {
  const vendorName = getVendorNameById(statement.vendor);
  const monthYear = formatMonthYear(statement.period_end);
  return vendorName && monthYear
    ? `${vendorName} - ${monthYear}`
    : statement.name || statement.file_name;
}

/** Aggregate the summary-card totals from a list of revenue statements. */
export function getRevenueTotals(statements: RevenueStatement[]): RevenueTotals {
  let totalRevenue = 0;
  let revenueReceived = 0;
  let pendingConfirmation = 0;
  for (const statement of statements) {
    const value = toNumber(statement.revenue);
    totalRevenue += value;
    if (statement.is_settled) {
      revenueReceived += toNumber(statement.amount_received);
    } else {
      pendingConfirmation += value;
    }
  }
  return { totalRevenue, revenueReceived, pendingConfirmation };
}
